// src/components/VoiceIntake.jsx
// Staff-facing patient intake: voice transcription (Sarvam AI) + translation,
// with the resulting session tracked in intake_sessions until moved into Visit
// Workflow. Self-service kiosk and phone intake feed the same table from their
// own entry points (see KioskIntake.jsx and the phone placeholder below).
import { useState, useEffect, useRef } from 'react';
import { createIntakeSession, updateIntakeSession, getOpenIntakeSessions, deleteIntakeSession, authFetch, getKioskToken, regenerateKioskToken, getIntakeDocuments, fetchIntakeDocumentBlob, downloadIntakeDocument, type IntakeDocument } from '../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/Dialog';
import { useSegmentedRecorder } from '../hooks/useSegmentedRecorder';
import { transcribeSegments } from '../utils/sarvam';
import { useAuth } from '../hooks/useAuth';
import { getVoiceLanguage, voiceLanguageLabel } from '../lib/voiceLanguages';
import { useAiAvailability } from '../features/ai-assist/useAiAvailability';

const MODE_META: Record<string, { icon: string; label: string }> = {
  receptionist: { icon:'🎙', label:'Receptionist' },
  kiosk:        { icon:'📱', label:'Self-service kiosk' },
  phone:        { icon:'📞', label:'Phone intake' },
  // Autosaved from Verify/Register while typed by hand, not recorded — see
  // features/registration/useIntakeDraft.ts.
  manual:       { icon:'📝', label:'Registration in progress' },
};

const OPEN_INTAKES_POLL_MS = 10000;

const DELETE_REASONS = [
  'Duplicate entry',
  'Test recording / mistake',
  'Patient left before registering',
  'Incorrect / garbled information',
  'Other',
];

interface ParsedIntakeData {
  name?: string;
  mobile?: string;
  gender?: string;
  age?: number | string;
  dob?: string;
  address?: string;
  complaints?: string;
  preferred_date?: string;
}

interface IntakeSession {
  id: number;
  mode: string;
  language_code: string;
  transcript?: string;
  translated_text?: string;
  parsed_data?: ParsedIntakeData | null;
  created_at: string;
  /** Uploads from the kiosk's scan-to-upload QR (GET /intake/open). */
  document_count?: number;
}

// Documents the patient uploaded against this intake from the kiosk (its
// scan-to-upload QR) — shown for review before starting the visit. They move
// onto the patient's record automatically once registration succeeds
// (backend utils/intakeDocuments.js), so there's nothing to action here.
function IntakeDocuments({ intakeId, documentCount }: { intakeId: number; documentCount?: number }) {
  const [docs, setDocs] = useState<IntakeDocument[] | null>(null);
  const [error, setError] = useState('');

  // documentCount comes from the auto-refreshing open-intakes list, so a file
  // the patient uploads while reception has this intake open shows up here too.
  useEffect(() => {
    let cancelled = false;
    setError('');
    getIntakeDocuments(intakeId)
      .then(d => { if (!cancelled) setDocs(d); })
      .catch(e => { if (!cancelled) { setDocs([]); setError(e instanceof Error ? e.message : String(e)); } });
    return () => { cancelled = true; };
  }, [intakeId, documentCount]);
  // A different intake starts from "Loading…", not the previous one's list.
  useEffect(() => { setDocs(null); }, [intakeId]);

  const [preview, setPreview] = useState<IntakeDocument | null>(null);
  const download = (docId: number) =>
    downloadIntakeDocument(intakeId, docId).catch(e => setError(e instanceof Error ? e.message : String(e)));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Uploaded documents</span>
      {docs === null && <div style={{ fontSize:12, color:'#9CA3AF' }}>Loading…</div>}
      {docs && docs.length === 0 && !error && <div style={{ fontSize:12, color:'#9CA3AF' }}>No documents uploaded for this intake.</div>}
      {error && <div style={{ fontSize:11.5, color:'#DC2626' }}>{error}</div>}
      {docs && docs.map(d => (
        <div key={d.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, padding:'6px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }}>
          <button type="button" onClick={() => setPreview(d)} title="Click to view"
            style={{ minWidth:0, flex:1, textAlign:'left', background:'none', border:'none', padding:0, cursor:'pointer' }}>
            <div style={{ fontSize:12.5, fontWeight:600, color:'var(--teal)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📄 {d.original_name}</div>
            <div style={{ fontSize:11, color:'#9CA3AF' }}>{d.doc_category} · {new Date(d.uploaded_at).toLocaleTimeString('en-IN')}</div>
          </button>
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            <button type="button" className="btn-ghost" style={{ padding:'4px 10px', fontSize:11.5 }} onClick={() => setPreview(d)}>👁 View</button>
            <button type="button" className="btn-success" style={{ padding:'4px 10px', fontSize:11.5 }} onClick={() => download(d.id)}>⬇ Download</button>
          </div>
        </div>
      ))}
      <IntakeDocumentPreview intakeId={intakeId} doc={preview} onClose={() => setPreview(null)} onDownload={download} />
    </div>
  );
}

// In-page preview (no new tab, so no popup blocker) — images and PDFs render
// directly; anything else falls back to Download. Same approach as
// DocumentPreviewButtons' file fallback, minus the OCR-text view: reception
// wants to see the actual prescription/report the patient brought.
// TIFF is an allowed upload, but only Safari can render it in an <img>.
const isBrowserImage = (contentType: string) => contentType.startsWith('image/') && !contentType.includes('tiff');

function IntakeDocumentPreview({ intakeId, doc, onClose, onDownload }: {
  intakeId: number;
  doc: IntakeDocument | null;
  onClose: () => void;
  onDownload: (docId: number) => void;
}) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'file'; url: string; contentType: string }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ kind: 'loading' });
    fetchIntakeDocumentBlob(intakeId, doc.id)
      .then(({ blob, contentType }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ kind: 'file', url: objectUrl, contentType });
      })
      .catch(e => { if (!cancelled) setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [intakeId, doc]);

  return (
    <Dialog open={!!doc} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doc?.original_name || 'Document'}</DialogTitle>
          <DialogDescription>{doc?.doc_category} · uploaded by the patient from the kiosk</DialogDescription>
        </DialogHeader>
        {state.kind === 'loading' && <div className="py-10 text-center text-sm text-ink-muted">Loading…</div>}
        {state.kind === 'error' && <div className="py-10 text-center text-sm text-danger-ink">Could not load this document: {state.message}</div>}
        {state.kind === 'file' && isBrowserImage(state.contentType) && (
          <img src={state.url} alt={doc?.original_name} className="max-h-[70vh] w-full rounded-[var(--radius)] border border-line object-contain" />
        )}
        {state.kind === 'file' && state.contentType.startsWith('application/pdf') && (
          <iframe src={state.url} title={doc?.original_name} className="h-[70vh] w-full rounded-[var(--radius)] border border-line" />
        )}
        {state.kind === 'file' && !isBrowserImage(state.contentType) && !state.contentType.startsWith('application/pdf') && (
          <div className="py-10 text-center text-sm text-ink-muted">This file type can't be previewed in the browser — use Download.</div>
        )}
        {doc && (
          <div className="flex justify-end">
            <button type="button" className="btn-success" style={{ padding:'6px 14px', fontSize:12.5 }} onClick={() => onDownload(doc.id)}>⬇ Download</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Small confirm popover asking WHY an open intake is being removed — kept
// inline rather than a full modal since it's a quick, low-stakes action off
// a list row, not a destructive data operation (the row is soft-deleted).
function DeleteIntakeConfirm({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState(DELETE_REASONS[0]);
  const [otherText, setOtherText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    const finalReason = reason === 'Other' ? otherText.trim() : reason;
    if (!finalReason) { setError('Please enter a reason'); return; }
    setDeleting(true); setError('');
    try { await onConfirm(finalReason); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setDeleting(false); }
  };

  return (
    <div onClick={e => e.stopPropagation()}
      style={{ marginTop:8, padding:'10px 12px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius)' }}>
      <div style={{ fontSize:12, fontWeight:600, color:'#991B1B', marginBottom:8 }}>Why are you deleting this intake?</div>
      <select value={reason} onChange={e => setReason(e.target.value)} style={{ fontSize:12, padding:'5px 8px', marginBottom:8, width:'100%' }}>
        {DELETE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
      </select>
      {reason === 'Other' && (
        <input value={otherText} onChange={e => setOtherText(e.target.value)} placeholder="Describe the reason…"
          style={{ fontSize:12, padding:'6px 8px', marginBottom:8, width:'100%' }} autoFocus />
      )}
      {error && <div style={{ fontSize:11, color:'#DC2626', marginBottom:8 }}>{error}</div>}
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn-danger" style={{ fontSize:11, padding:'4px 10px' }} disabled={deleting} onClick={confirm}>
          {deleting ? 'Deleting…' : 'Delete intake'}
        </button>
        <button className="btn-ghost" style={{ fontSize:11, padding:'4px 10px' }} onClick={onCancel} disabled={deleting}>Cancel</button>
      </div>
    </div>
  );
}

interface VoiceIntakeProps {
  onIntakeComplete?: (parsed: ParsedIntakeData) => void;
}

export default function VoiceIntake({ onIntakeComplete }: VoiceIntakeProps) {
  // AI off (§4.4): the recording panel is hidden — "Fill in manually" (the
  // Patient details card alongside it) is always available either way.
  const { aiEnabled } = useAiAvailability();
  // Defaults to the tenant-wide voice language (Admin Panel -> Branding ->
  // Voice input), but still needs to be settable, not just read-once: resuming
  // an open intake (resumeIntake below) has to switch to whatever language
  // that particular recording actually used, which may differ from the
  // current default if it was recorded earlier, elsewhere, or before the
  // default changed. This is session fidelity, not a user preference picker.
  const [lang, setLang]         = useState(getVoiceLanguage);
  const [processing, setProc]   = useState(false);
  const [translating, setTranslating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [parsed, setParsed]     = useState<ParsedIntakeData | null>(null);
  const [fields, setFields]     = useState<ParsedIntakeData>({});
  const [error, setError]       = useState('');
  const [intakeId, setIntakeId] = useState<number | null>(null);
  const [openIntakes, setOpenIntakes] = useState<IntakeSession[] | null>(null);
  const [openingKiosk, setOpeningKiosk] = useState(false);
  const [kioskError, setKioskError] = useState('');
  const [regenConfirm, setRegenConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [justResumed, setJustResumed] = useState(false);
  const { user, branchVersion } = useAuth();
  const { recording, timeElapsed, segments, totalBytes, error: recError, start: startRec, stop: stopRec, reset: resetRecorder } = useSegmentedRecorder();
  const audioBlob = segments.length > 0 ? { size: totalBytes } : null; // render-only stand-in for "has a recording"
  const resumedSectionsRef = useRef<HTMLDivElement>(null);

  const loadOpenIntakes = () => getOpenIntakeSessions().then(setOpenIntakes).catch(() => setOpenIntakes([]));
  useEffect(() => { loadOpenIntakes(); }, [branchVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  // A patient finishing at the kiosk (or uploading from their phone) should
  // appear here without anyone pressing Refresh. Same plain polling as
  // StatusBoard/QueueStatusPage; skipped while the tab is hidden so a
  // background tab doesn't keep hitting the API.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) loadOpenIntakes(); }, OPEN_INTAKES_POLL_MS);
    const onVisible = () => { if (!document.hidden) loadOpenIntakes(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [branchVersion]);
  useEffect(() => () => { stopRecording(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Picking an open intake off the list above jumps straight to its
  // Transcript/Translation/Extracted-data sections further down the page —
  // otherwise staff have to manually scroll past the (now-irrelevant)
  // recording panel every time to see what was actually captured.
  useEffect(() => {
    if (!justResumed) return;
    resumedSectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const t = setTimeout(() => setJustResumed(false), 1600);
    return () => clearTimeout(t);
  }, [justResumed]);

  const resetSession = () => {
    setIntakeId(null); setParsed(null); setFields({}); setError(''); resetRecorder();
  };

  const startRecording = async () => {
    resetSession();
    await startRec();
  };

  const stopRecording = () => { stopRec(); };

  // The kiosk has no login of its own — it authenticates as "the kiosk
  // terminal" via a shared device token instead (see backend/utils/kioskAuth.js).
  // Fetch it here and embed it in the URL so opening kiosk mode still works
  // with a single click. Admin-only server-side (audit-flagged: the token
  // never expires, so any role able to read it could reach the kiosk booking
  // API from anywhere on the internet) — a non-Admin sees the 403 surfaced
  // below as kioskError and should ask an Admin to open/share the link.
  const openKiosk = async () => {
    setOpeningKiosk(true); setKioskError('');
    try {
      const { token } = await getKioskToken();
      window.open(`${window.location.origin}?kiosk=1&token=${token}`, '_blank');
    } catch (e) { setKioskError(e instanceof Error ? e.message : String(e)); }
    setOpeningKiosk(false);
  };

  const doRegenerateKioskToken = async () => {
    setRegenerating(true); setKioskError('');
    try { await regenerateKioskToken(); setRegenConfirm(false); }
    catch (e) { setKioskError(e instanceof Error ? e.message : String(e)); }
    setRegenerating(false);
  };

  const doDeleteIntake = async (id: number, reason: string) => {
    await deleteIntakeSession(id, reason);
    setDeleteConfirmId(null);
    if (intakeId === id) resetSession();
    loadOpenIntakes();
  };

  const transcribe = async () => {
    if (!segments.length) return;
    setProc(true); setError(''); setParsed(null);
    try {
      const combined = await transcribeSegments(segments, lang, 'receptionist');
      if (!combined) { setError('No speech was detected in that recording. Try again — speak clearly and record for at least a few seconds.'); setProc(false); return; }

      const session = await createIntakeSession({ mode:'receptionist', language_code: lang, audio_duration_sec: timeElapsed });
      setIntakeId(session.id);
      await updateIntakeSession(session.id, { transcript: combined });
      loadOpenIntakes();

      // English recordings need no translation — extract directly. Others run
      // straight through translate() below; none of this is shown to the user,
      // it just lands as filled-in fields in the Patient details card.
      if (lang === 'en-IN') {
        await extractFrom(combined, session.id);
      } else {
        await translate(combined, session.id);
      }
    } catch(e) {
      setError('Transcription failed: ' + (e instanceof Error ? e.message : String(e)));
    }
    setProc(false);
  };

  const translate = async (sourceText: string, forIntakeId: number | null) => {
    setTranslating(true); setError('');
    try {
      const res  = await authFetch('/api/voice/translate', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ text: sourceText, source_language_code: lang, target_language_code: 'en-IN' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      if (forIntakeId) await updateIntakeSession(forIntakeId, { translated_text: data.translated_text });
      await extractFrom(data.translated_text, forIntakeId);
    } catch(e) {
      setError('Translation failed: ' + (e instanceof Error ? e.message : String(e)));
    }
    setTranslating(false);
  };

  const extractFrom = async (englishText: string, forIntakeId: number | null) => {
    setExtracting(true);
    try {
      const res = await authFetch('/api/sarvam/extract-intent', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ transcript: englishText, language_code: 'en-IN' }),
      });
      const data = await res.json();
      setParsed(data);
      setFields(f => ({ ...data, ...f })); // voice fills blanks; never clobbers what staff already typed
      if (forIntakeId) await updateIntakeSession(forIntakeId, { parsed_data: data });
      loadOpenIntakes();
    } catch(e) {
      setError('Field extraction failed: ' + (e instanceof Error ? e.message : String(e)));
    }
    setExtracting(false);
  };

  const resumeIntake = (session: IntakeSession) => {
    resetSession();
    setIntakeId(session.id);
    setLang(session.language_code);
    setParsed(session.parsed_data || null);
    setFields(session.parsed_data || {});
    setJustResumed(true);
  };

  const setField = (key: keyof ParsedIntakeData, value: string) => {
    setFields(f => ({ ...f, [key]: value }));
  };

  const startVisitWithData = async () => {
    // Register-stage demographic fields, consumed once there and cleared.
    window.__voiceIntakeData = {
      name:    fields.name    || '',
      mobile:  fields.mobile  || '',
      gender:  fields.gender  || '',
      age:     fields.age     ? String(fields.age) : '',
      dob:     fields.dob     || '',
      address: fields.address || '',
    };
    // The chief complaint belongs on Vitals' "Symptoms / complaints" field
    // instead — a separate global since it needs to survive past Register,
    // which clears __voiceIntakeData once it's consumed the fields above.
    window.__voiceIntakeComplaints = fields.complaints || null;
    // Deliberately NOT marked 'converted' here — only that a patient/visit
    // was actually created from it makes an intake "done". Marking it here
    // instead (the previous behavior) dropped it out of the Open Intakes
    // list the instant this button was clicked, so an abandoned Verify/
    // Register (browser closed, staff pulled away, network hiccup) had no
    // way back to it even though nothing was ever actually registered.
    // VisitWorkflow.jsx marks it converted once registration truly succeeds.
    if (intakeId) {
      await updateIntakeSession(intakeId, { parsed_data: fields }).catch(() => {});
      window.__voiceIntakeSessionId = intakeId;
    }
    onIntakeComplete?.(fields);
  };

  const formatTime = (s: number) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const langLabel = voiceLanguageLabel(lang);

  return (
    <div className="fade-in" style={{ maxWidth: 1040 }}>

      {/* Open intakes not yet moved to Visit Workflow */}
      {openIntakes === null && (
        <div style={{ textAlign:'center', padding:20, color:'#9CA3AF', fontSize:13 }}>Loading open intakes…</div>
      )}
      {openIntakes && openIntakes.length > 0 && (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontWeight:600, fontSize:14 }}>Open intakes — not yet moved to Visit Workflow</div>
            <button className="btn-ghost" style={{ padding:'4px 10px', fontSize:12 }} onClick={loadOpenIntakes}>↻ Refresh</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {openIntakes.map(s => (
              <div key={s.id}>
                <div onClick={() => resumeIntake(s)}
                  style={{ padding:'10px 12px', border:`1px solid ${intakeId===s.id?'var(--teal)':'#E5E7EB'}`, borderRadius:'var(--radius)', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor='var(--teal)'}
                  onMouseLeave={e=>{ if(intakeId!==s.id) e.currentTarget.style.borderColor='#E5E7EB'; }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13 }}>{s.parsed_data?.name || '(name not extracted)'}</div>
                    <div style={{ fontSize:11, color:'#6B7280', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:400 }}>
                      {s.translated_text || s.transcript || 'No transcript yet'}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    {!!s.document_count && (
                      <span className="badge badge-info" title="Documents uploaded from the kiosk">📎 {s.document_count}</span>
                    )}
                    <span className="badge badge-info">{MODE_META[s.mode]?.icon} {MODE_META[s.mode]?.label}</span>
                    <span style={{ fontSize:11, color:'#9CA3AF' }}>{new Date(s.created_at).toLocaleTimeString('en-IN')}</span>
                    <button title="Delete this intake" onClick={e => { e.stopPropagation(); setDeleteConfirmId(s.id); }}
                      style={{ background:'none', border:'none', cursor:'pointer', fontSize:14, padding:'2px 4px', color:'#9CA3AF' }}>
                      🗑
                    </button>
                    <span style={{ color:'var(--teal)', fontSize:18 }}>→</span>
                  </div>
                </div>
                {deleteConfirmId === s.id && (
                  <DeleteIntakeConfirm
                    onCancel={() => setDeleteConfirmId(null)}
                    onConfirm={(reason) => doDeleteIntake(s.id, reason)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recording language is a tenant-wide default now (Admin Panel ->
          Branding -> Voice input), not a per-screen picker — see
          lib/voiceLanguages.ts. Still shown so staff know what's being
          listened for, just no longer editable here. */}
      {aiEnabled && (
        <div style={{ marginBottom:16, fontSize:12, color:'#6B7280' }}>
          🎙 Recording in <strong>{langLabel}</strong> — change the default in Admin Panel → Branding → Voice input.
        </div>
      )}

      {/* Recording panel + Patient details side by side on desktop so the
          editable fields stay visible/accessible without scrolling past the
          recorder; stacks to a single column on narrow viewports. */}
      <div className={aiEnabled ? 'grid md:grid-cols-2 gap-4 items-start' : undefined} style={{ marginBottom:16 }}>
      {/* Recording panel — h-full + flex so it stretches to match the Patient
          details card's height in the row rather than leaving a gap below it.
          Hidden entirely when AI is off (§4.4) — "fill in manually" below is
          always available either way. */}
      {aiEnabled && (
      <div className="card h-full" style={{ textAlign:'center', padding:32, display:'flex', flexDirection:'column', justifyContent:'center' }}>
        {/* Waveform animation */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:3, height:48, marginBottom:16 }}>
          {Array.from({length:20}).map((_,i)=>(
            <div key={i} style={{
              width:4, borderRadius:2,
              background: recording ? 'var(--teal)' : '#E5E7EB',
              height: recording ? `${16 + Math.sin(Date.now()/200+i)*16}px` : '4px',
              transition: 'height .1s ease',
              animation: recording ? `wave ${0.5+i*0.05}s ease-in-out infinite alternate` : 'none',
            }} />
          ))}
        </div>

        {recording && (
          <div style={{ fontSize:14, color:'var(--teal)', fontWeight:600, marginBottom:12 }}>
            🔴 Recording… {formatTime(timeElapsed)}
          </div>
        )}

        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
          {!recording && !audioBlob && (
            <button className="btn-primary" onClick={startRecording} style={{ padding:'16px 36px', fontSize:17 }}>
              🎙 Start recording
            </button>
          )}
          {recording && (
            <button onClick={stopRecording} style={{ padding:'16px 36px', fontSize:17, background:'#DC2626', color:'#fff', border:'none', borderRadius:'var(--radius)', cursor:'pointer', fontWeight:600 }}>
              ⏹ Stop recording
            </button>
          )}
          {audioBlob && !recording && !processing && (
            <>
              <button className="btn-ghost" onClick={resetSession}>
                🔄 Re-record
              </button>
              <button className="btn-primary" onClick={transcribe} style={{ padding:'16px 36px', fontSize:17 }}>
                🔍 Transcribe Audio
              </button>
            </>
          )}
          {(processing || translating || extracting) && (
            <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:'#6B7280' }}>
              <span style={{ animation:'pulse 1s infinite', display:'inline-block' }}>⏳</span>
              {extracting ? 'Extracting patient details…' : translating ? 'Translating…' : `Transcribing audio (${langLabel})…`}
            </div>
          )}
        </div>

        {audioBlob && !recording && (
          <div style={{ marginTop:12, fontSize:12, color:'#9CA3AF' }}>
            Recording ready · {formatTime(timeElapsed)} · {(audioBlob.size/1024).toFixed(0)} KB
          </div>
        )}

        {!recording && !audioBlob && (
          <div style={{ marginTop:20, fontSize:12, color:'#6B7280', lineHeight:1.7, maxWidth:320, marginLeft:'auto', marginRight:'auto' }}>
            💡 Speak clearly in the selected language and mention the patient's <b>name</b>, <b>mobile number</b>, and <b>reason for visit</b> — these are auto-filled into the form once transcribed.
          </div>
        )}
      </div>
      )}

      {/* Patient details — name/mobile/health issue always editable here, whether
          typed directly or auto-populated by voice extraction below (age too). Any other
          field voice manages to pull out (gender, dob, address, preferred
          date) is added below as its own editable row, per BAU extraction logic. */}
      <div className="card" style={{ transition:'box-shadow .3s, border-color .3s', boxShadow: justResumed ? '0 0 0 3px rgba(11,95,214,.25)' : 'none', borderColor: justResumed ? 'var(--teal)' : undefined }}>
        <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Patient details</div>
        <div style={{ fontSize:11.5, color:'#9CA3AF', marginBottom:12 }}>
          {parsed ? 'Auto-filled from voice — review and edit before starting the visit.' : aiEnabled ? 'Fill in manually, or record to auto-fill.' : 'Fill in manually.'}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Patient name</span>
            <input value={fields.name || ''} onChange={e=>setField('name', e.target.value)} placeholder="Full name"
              style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
          </label>
          <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Mobile number</span>
            <input value={fields.mobile || ''} onChange={e=>setField('mobile', e.target.value)} placeholder="10-digit mobile number"
              style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
          </label>
          <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Age</span>
            <input type="number" min={0} max={120} inputMode="numeric"
              value={fields.age !== undefined && fields.age !== null ? String(fields.age) : ''} onChange={e=>setField('age', e.target.value)} placeholder="Age in years"
              style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
          </label>
          <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Health issue / chief complaint</span>
            <textarea value={fields.complaints || ''} onChange={e=>setField('complaints', e.target.value)} placeholder="What is the patient here for?" rows={2}
              style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)', fontFamily:'inherit', resize:'vertical' }} />
          </label>

          {/* Additional fields — only shown once voice extraction actually surfaced them */}
          {parsed?.gender && (
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Gender</span>
              <input value={fields.gender || ''} onChange={e=>setField('gender', e.target.value)}
                style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
            </label>
          )}
          {parsed?.dob && (
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Date of birth</span>
              <input value={fields.dob || ''} onChange={e=>setField('dob', e.target.value)}
                style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
            </label>
          )}
          {parsed?.address && (
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Address</span>
              <input value={fields.address || ''} onChange={e=>setField('address', e.target.value)}
                style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
            </label>
          )}
          {parsed?.preferred_date && (
            <label style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:12, fontWeight:600, color:'#6B7280' }}>Preferred date</span>
              <input value={fields.preferred_date || ''} onChange={e=>setField('preferred_date', e.target.value)}
                style={{ fontSize:13, padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:'var(--radius)' }} />
            </label>
          )}
          {/* Only for a resumed intake — a fresh recording has nothing
              uploaded against it yet. */}
          {intakeId != null && (
            <IntakeDocuments intakeId={intakeId} documentCount={openIntakes?.find(s => s.id === intakeId)?.document_count} />
          )}
        </div>
        <div style={{ marginTop:14 }}>
          <button className="btn-primary" onClick={startVisitWithData} disabled={!fields.name && !fields.mobile}>
            → Start visit with this data
          </button>
        </div>
      </div>
      </div>

      {(error || recError) && <div style={{ padding:'10px 14px', background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:'var(--radius)', color:'#991B1B', fontSize:13, marginBottom:12 }}>{error || recError}</div>}

      {/* Transcription/translation/extraction all run in the background (see
          transcribe()/translate()/extractFrom() above) — only the resulting
          fields are surfaced, in the Patient details card above. This anchor
          is still where an open intake resumed from the list scrolls to. */}
      <div ref={resumedSectionsRef} />


      {/* Other intake channels */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:8 }}>
        <div className="card" style={{ padding:16 }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>📱 Self-service kiosk</div>
          <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.6, marginBottom:10 }}>
            A dedicated full-screen intake flow for a reception-desk tablet or kiosk terminal — patients record their own intake without staff assistance. The link includes this hospital's kiosk access token — bookmark it on the tablet rather than sharing it further.
          </div>
          {kioskError && <div style={{ fontSize:11.5, color:'#DC2626', marginBottom:8 }}>{kioskError}</div>}
          {user?.role === 'Admin' ? (
            <button className="btn-ghost" style={{ fontSize:12, padding:'6px 12px' }} disabled={openingKiosk} onClick={openKiosk}>
              {openingKiosk ? 'Opening…' : 'Open kiosk mode →'}
            </button>
          ) : (
            <div style={{ fontSize:11.5, color:'#6B7280' }}>Ask an Admin to open or share the kiosk link — the access token is Admin-only.</div>
          )}
          {user?.role === 'Admin' && (
            regenConfirm ? (
              <div style={{ marginTop:10, padding:'8px 10px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'var(--radius)' }}>
                <div style={{ fontSize:11.5, color:'#92400E', marginBottom:6 }}>⚠ This invalidates any kiosk tablet already bookmarked with the current link. Continue?</div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn-danger" style={{ fontSize:11, padding:'4px 10px' }} disabled={regenerating} onClick={doRegenerateKioskToken}>
                    {regenerating ? '…' : 'Confirm regenerate'}
                  </button>
                  <button className="btn-ghost" style={{ fontSize:11, padding:'4px 10px' }} onClick={() => setRegenConfirm(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="btn-ghost" style={{ fontSize:11, padding:'4px 10px', marginLeft:8 }} onClick={() => setRegenConfirm(true)}>
                🔄 Regenerate access token
              </button>
            )
          )}
        </div>
        <div className="card" style={{ padding:16 }}>
          <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>📞 Phone intake</div>
          <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.6 }}>
            Not yet connected to a telephony provider. Planned: an inbound-call recording webhook feeds the same transcribe → translate → extract pipeline, landing here as an open intake with mode "phone".
          </div>
        </div>
      </div>

      <style>{`@keyframes wave { from { transform:scaleY(1) } to { transform:scaleY(2.5) } }`}</style>
    </div>
  );
}
